import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiConsumer } from '../../service/api-consumer';
import { OperatorDetailsRequest } from '../../interface/request/OperatorDetailsRequest';
import { OperatorDetailsResponse } from '../../interface/response/OperatorDetailsResponse';
import { Name } from '../../interface/response/CashRegisterDataResponse';

/**
 * Async thunk to fetch operator details from the API.
 *
 * @function fetchOperatorDetails
 * @param {OperatorDetailsRequest} payload - The request parameters for fetching operator details.
 * @returns {Promise<OperatorDetailsResponse>} - Returns a promise that resolves to the operator details response.
 *
 * @example
 * dispatch(fetchOperatorDetails({ operatorId: 123 }));
 *
 * @remarks
 * - Uses `ApiConsumer.getOperatorDetails` to fetch data.
 * - The returned data is handled in the slice's `extraReducers`.
 *
 * Shape of the Redux state for operator details.
 *
 * @interface DataState
 * @property {OperatorDetailsResponse} data - Last loaded operator details.
 * @property {boolean} loading - Indicates whether the request is currently in progress.
 * @property {unknown | null} error - Contains an error object if the last request failed.
 * @property {Name} selectedOperator - The currently selected operator.
 * @property {boolean} operatorModalOpen - Flag to control the visibility of the operator modal.
 *
 * Redux slice responsibilities:
 * - Fetching operator details using `fetchOperatorDetails`
 * - Storing loading, error, and data state
 * - Managing selected operator and modal visibility
 * - Providing reducers for modal and selected operator updates
 *
 * Redux slice for managing operator details state.
 *
 * @constant operatorDetailsSlice
 *
 * @remarks
 * - Uses `createSlice` from Redux Toolkit.
 * - Reducers:
 *   - `setOperatorModalOpen`: Opens or closes the operator modal.
 *   - `setSelectedOperator`: Updates the currently selected operator.
 * - Extra reducers handle lifecycle of `fetchOperatorDetails` thunk:
 *   - `pending`: sets `loading` to true, clears errors
 *   - `fulfilled`: updates `data` with fetched operator details, sets `loading` to false
 *   - `rejected`: sets `error` with the received error, sets `loading` to false
 *
 * @example
 * dispatch(setOperatorModalOpen(true));
 * dispatch(setSelectedOperator({ UserName: 'jdoe', FullName: 'John Doe' }));
 * dispatch(fetchOperatorDetails({ operatorId: 123 }));
 */

export const fetchOperatorDetails = createAsyncThunk(
  'data/fetchOperatorDetails',
  async (payload: OperatorDetailsRequest) => {
    const response = await ApiConsumer.getOperatorDetails(payload);
    return response;
  },
);
interface DataState {
  data: OperatorDetailsResponse;
  loading: boolean;
  error: null | unknown;
  selectedOperator: Name;
  operatorModalOpen: boolean;
}

const initialState: DataState = {
  data: {} as OperatorDetailsResponse,
  loading: false,
  error: null,
  selectedOperator: { UserName: '', FullName: '' },
  operatorModalOpen: false,
};

const operatorDetailsSlice = createSlice({
  name: 'operatorDetails',
  initialState,
  reducers: {
    setOperatorModalOpen: (state, action) => {
      state.operatorModalOpen = action.payload;
    },
    setSelectedOperator: (state, action) => {
      state.selectedOperator = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOperatorDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOperatorDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchOperatorDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      });
  },
});

export const { setOperatorModalOpen, setSelectedOperator } = operatorDetailsSlice.actions;
export default operatorDetailsSlice.reducer;
